<script setup>
// Template builder. Every limit enforced here comes from Meta's official template documentation,
// and the dialog refuses to submit while any of them is broken: a rejected template is a mark on
// the client's WhatsApp Business Account, so it is cheaper to block locally than to be refused.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DialogSteps from 'dashboard/wasapi/common/DialogSteps.vue';
import TemplatePreview from './TemplatePreview.vue';
import {
  BUTTON_TYPES,
  CATEGORIES,
  CODE_EXPIRATION_RANGE,
  OTP_TYPES,
  isAuthentication,
  EDITABLE_STATUSES,
  HEADER_FORMATS,
  LANGUAGES,
  LIMITS,
  MEDIA_ACCEPT,
  buildComponents,
  emptyForm,
  formFromTemplate,
  slugifyName,
  validateTemplate,
  variablesIn,
} from '../helpers';

const props = defineProps({
  isSaving: { type: Boolean, default: false },
  isUploading: { type: Boolean, default: false },
  // { remaining, limit } from the last sync: how many templates Meta still lets us create this hour.
  quota: { type: Object, default: null },
  // Meta's own refusal, shown inside the dialog: a toast disappears before the client can read
  // why a submission was rejected, and the dialog stays open with the form intact.
  apiError: { type: String, default: '' },
  // Which WhatsApp Cloud inbox receives the new template (hidden while editing).
  inboxOptions: { type: Array, default: () => [] },
  inboxId: { type: [Number, String], default: null },
});

const emit = defineEmits(['save', 'upload', 'update:inboxId']);

const { t } = useI18n();

const dialogRef = ref(null);
const step = ref(1);
const form = ref(emptyForm());
const editing = ref(null);
const fileInput = ref(null);

const isEditing = computed(() => !!editing.value);
const isApproved = computed(() => editing.value?.status === 'APPROVED');
const canEdit = computed(
  () => !isEditing.value || EDITABLE_STATUSES.includes(editing.value.status)
);

const stepOptions = computed(() => [
  { value: 1, label: t('WASAPI_TEMPLATES.DIALOG.STEP_BASICS') },
  { value: 2, label: t('WASAPI_TEMPLATES.DIALOG.STEP_CONTENT') },
]);
const categoryOptions = computed(() =>
  CATEGORIES.map(value => ({
    value,
    label: t(`WASAPI_TEMPLATES.CATEGORY.${value}`),
  }))
);
const languageOptions = computed(() =>
  LANGUAGES.map(value => ({ value, label: value }))
);
const headerOptions = computed(() =>
  HEADER_FORMATS.map(value => ({
    value,
    label: t(`WASAPI_TEMPLATES.TYPES.${value}`),
  }))
);
const buttonTypeOptions = computed(() =>
  BUTTON_TYPES.map(value => ({
    value,
    label: t(`WASAPI_TEMPLATES.TYPES.${value}`),
  }))
);

const isAuth = computed(() => isAuthentication(form.value));
const otpTypeOptions = computed(() =>
  OTP_TYPES.map(value => ({
    value,
    label: t(`WASAPI_TEMPLATES.TYPES.${value}`),
  }))
);
const bodyVariables = computed(() => variablesIn(form.value.body));
const headerVariables = computed(() => variablesIn(form.value.headerText));
const mediaAccept = computed(() => MEDIA_ACCEPT[form.value.headerFormat] || '');

const errors = computed(() => validateTemplate(form.value));
// Deduplicated: the same rule broken on three buttons is still one thing to fix.
const errorMessages = computed(() => [
  ...new Set(
    errors.value.map(error =>
      t(`WASAPI_TEMPLATES.ERRORS.${error.key}`, error.params)
    )
  ),
]);
const quotaExhausted = computed(
  () => !editing.value && props.quota && props.quota.remaining <= 0
);
const canSubmit = computed(
  () => !errors.value.length && canEdit.value && !quotaExhausted.value
);

// Backdrop clicks and Escape must not throw away a filled form: the dialog turns
// persistent as soon as it differs from what it opened with.
const openedSnapshot = ref('');
const isDirty = computed(
  () => JSON.stringify(form.value) !== openedSnapshot.value
);

// Keeps the example array aligned with the variables actually present in the body.
watch(bodyVariables, found => {
  form.value.bodyExamples = found.map(
    (_, index) => form.value.bodyExamples[index] || ''
  );
});

const open = (template = null) => {
  // A duplicate arrives with the content but no id: it has to behave like a new template,
  // otherwise the name stays locked and the copy can never be given one.
  editing.value = template?.id ? template : null;
  form.value = template ? formFromTemplate(template) : emptyForm();
  step.value = 1;
  openedSnapshot.value = JSON.stringify(form.value);
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onNameInput = value => {
  form.value.name = slugifyName(value);
};

const addButton = () => {
  form.value.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phoneNumber: '',
    example: '',
  });
};

const removeButton = index => form.value.buttons.splice(index, 1);

const pickFile = () => fileInput.value?.click();

const onFileChange = async event => {
  const [file] = event.target.files || [];
  if (!file) return;
  form.value.headerFileName = file.name;
  emit('upload', {
    file,
    onDone: handle => {
      form.value.headerHandle = handle;
    },
  });
  event.target.value = '';
};

const submit = () => {
  if (!canSubmit.value) return;
  emit('save', {
    template: {
      name: form.value.name,
      category: form.value.category,
      language: form.value.language,
      components: buildComponents(form.value),
    },
    editing: editing.value,
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="
      isEditing
        ? t('WASAPI_TEMPLATES.DIALOG.EDIT_TITLE')
        : t('WASAPI_TEMPLATES.DIALOG.NEW_TITLE')
    "
    :show-confirm-button="false"
    :show-cancel-button="false"
    :persistent="isDirty"
    overflow-y-auto
  >
    <div class="flex flex-col gap-4">
      <DialogSteps v-model="step" :steps="stepOptions" />

      <div
        v-if="!canEdit"
        class="flex items-start gap-2 p-2.5 text-xs rounded-lg bg-n-ruby-3/50 text-n-ruby-11"
      >
        <Icon icon="i-lucide-lock" class="size-3.5 shrink-0 mt-0.5" />
        <span>{{ t('WASAPI_TEMPLATES.NOTICE.NOT_EDITABLE') }}</span>
      </div>

      <div v-show="step === 1" class="flex flex-col gap-4">
        <div
          v-if="!isEditing && inboxOptions.length > 1"
          class="flex flex-col gap-1 text-sm text-n-slate-12"
        >
          {{ t('WASAPI_TEMPLATES.DIALOG.INBOX') }}
          <ComboBox
            :model-value="inboxId"
            :options="inboxOptions"
            @update:model-value="emit('update:inboxId', $event)"
          />
        </div>
        <Input
          :model-value="form.name"
          :label="t('WASAPI_TEMPLATES.DIALOG.NAME')"
          :message="t('WASAPI_TEMPLATES.DIALOG.NAME_HINT')"
          :disabled="isEditing"
          @update:model-value="onNameInput"
        />
        <div class="grid grid-cols-2 gap-4">
          <!-- ComboBox never goes inside a <label>: the label forwards the click and reopens it. -->
          <div class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('WASAPI_TEMPLATES.DIALOG.LANGUAGE') }}
            <ComboBox v-model="form.language" :options="languageOptions" />
          </div>
          <div class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('WASAPI_TEMPLATES.DIALOG.CATEGORY') }}
            <ComboBox
              v-model="form.category"
              :options="categoryOptions"
              :disabled="isApproved"
            />
          </div>
        </div>
        <p class="text-xs text-n-slate-11">
          {{ t(`WASAPI_TEMPLATES.CATEGORY_HINT.${form.category}`) }}
        </p>
        <div
          class="flex flex-col gap-2 p-2.5 text-xs rounded-lg bg-n-amber-3/50 text-n-amber-11"
        >
          <span class="flex items-start gap-2">
            <Icon
              icon="i-lucide-triangle-alert"
              class="size-3.5 shrink-0 mt-0.5"
            />
            {{ t('WASAPI_TEMPLATES.NOTICE.REVIEW') }}
          </span>
          <span v-if="form.category === 'MARKETING'" class="pl-5.5">
            {{ t('WASAPI_TEMPLATES.NOTICE.MARKETING_OPT_IN') }}
          </span>
          <span
            v-if="!isEditing && quota"
            class="pl-5.5"
            :class="{ 'text-n-ruby-11': quotaExhausted }"
          >
            {{
              quotaExhausted
                ? t('WASAPI_TEMPLATES.DIALOG.QUOTA_EXHAUSTED')
                : t('WASAPI_TEMPLATES.DIALOG.QUOTA', quota)
            }}
          </span>
          <span v-if="form.category === 'AUTHENTICATION'" class="pl-5.5">
            {{ t('WASAPI_TEMPLATES.NOTICE.AUTHENTICATION_ONLY_CODE') }}
          </span>
          <span v-if="isEditing" class="pl-5.5">
            {{ t('WASAPI_TEMPLATES.NOTICE.EDIT_LIMIT') }}
          </span>
          <span v-if="isApproved" class="pl-5.5">
            {{ t('WASAPI_TEMPLATES.NOTICE.CATEGORY_LOCKED') }}
          </span>
        </div>
      </div>

      <div v-show="step === 2" class="grid grid-cols-5 gap-5">
        <div v-if="isAuth" class="flex flex-col gap-4 col-span-3">
          <p class="text-xs text-n-slate-11">
            {{ t('WASAPI_TEMPLATES.DIALOG.AUTH_HINT') }}
          </p>
          <label class="inline-flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="form.securityRecommendation"
              type="checkbox"
              class="!mb-0"
            />
            {{ t('WASAPI_TEMPLATES.DIALOG.SECURITY_RECOMMENDATION') }}
          </label>
          <Input
            v-model="form.codeExpirationMinutes"
            type="number"
            :label="t('WASAPI_TEMPLATES.DIALOG.CODE_EXPIRATION')"
            :message="
              t(
                'WASAPI_TEMPLATES.DIALOG.CODE_EXPIRATION_HINT',
                CODE_EXPIRATION_RANGE
              )
            "
          />
          <div class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('WASAPI_TEMPLATES.DIALOG.OTP_TYPE') }}
            <ComboBox v-model="form.otpType" :options="otpTypeOptions" />
          </div>
          <Input
            v-model="form.otpButtonText"
            :label="t('WASAPI_TEMPLATES.DIALOG.OTP_TEXT')"
          />
        </div>

        <div v-else class="flex flex-col gap-4 col-span-3">
          <div class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('WASAPI_TEMPLATES.DIALOG.HEADER_FORMAT') }}
            <ComboBox v-model="form.headerFormat" :options="headerOptions" />
          </div>

          <template v-if="form.headerFormat === 'TEXT'">
            <Input
              v-model="form.headerText"
              :label="t('WASAPI_TEMPLATES.DIALOG.HEADER_TEXT')"
              :message="`${form.headerText.length}/${LIMITS.headerTextMax}`"
            />
            <Input
              v-if="headerVariables.length"
              v-model="form.headerExample"
              :label="t('WASAPI_TEMPLATES.DIALOG.HEADER_EXAMPLE')"
            />
          </template>

          <div
            v-else-if="form.headerFormat !== 'NONE'"
            class="flex flex-col gap-1"
          >
            <span class="text-sm text-n-slate-12">
              {{ t('WASAPI_TEMPLATES.DIALOG.HEADER_FILE') }}
            </span>
            <div class="flex items-center gap-2">
              <Button
                variant="faded"
                color="slate"
                size="sm"
                :label="
                  isUploading
                    ? t('WASAPI_TEMPLATES.DIALOG.UPLOADING')
                    : t('WASAPI_TEMPLATES.DIALOG.UPLOAD')
                "
                :disabled="isUploading"
                @click="pickFile"
              />
              <span v-if="form.headerFileName" class="text-xs text-n-slate-11">
                {{ form.headerFileName }}
              </span>
              <Icon
                v-if="form.headerHandle"
                icon="i-lucide-check"
                class="size-4 text-n-teal-11"
              />
            </div>
            <span class="text-xs text-n-slate-11">
              {{ t('WASAPI_TEMPLATES.DIALOG.HEADER_FILE_HINT') }}
            </span>
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              :accept="mediaAccept"
              @change="onFileChange"
            />
          </div>

          <TextArea
            v-model="form.body"
            :label="t('WASAPI_TEMPLATES.DIALOG.BODY')"
            :message="`${form.body.length}/${LIMITS.bodyMax}`"
            class="[&>textarea]:min-h-24"
          />
          <span class="-mt-2 text-xs text-n-slate-11">
            {{ t('WASAPI_TEMPLATES.DIALOG.BODY_HINT') }}
          </span>

          <div v-if="bodyVariables.length" class="flex flex-col gap-2">
            <span class="text-sm text-n-slate-12">
              {{ t('WASAPI_TEMPLATES.DIALOG.EXAMPLES') }}
            </span>
            <Input
              v-for="(variable, index) in bodyVariables"
              :key="variable + index"
              v-model="form.bodyExamples[index]"
              :placeholder="
                t('WASAPI_TEMPLATES.DIALOG.EXAMPLE_FOR', {
                  variable: `{{${variable}}}`,
                })
              "
            />
          </div>

          <Input
            v-model="form.footer"
            :label="t('WASAPI_TEMPLATES.DIALOG.FOOTER')"
            :message="`${form.footer.length}/${LIMITS.footerMax}`"
          />

          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-12">
                {{ t('WASAPI_TEMPLATES.DIALOG.BUTTONS') }}
              </span>
              <Button
                variant="faded"
                color="slate"
                size="sm"
                icon="i-lucide-plus"
                :label="t('WASAPI_TEMPLATES.DIALOG.ADD_BUTTON')"
                :disabled="form.buttons.length >= LIMITS.buttonsMax"
                @click="addButton"
              />
            </div>
            <div
              v-for="(button, index) in form.buttons"
              :key="index"
              class="flex flex-col gap-2 p-2 rounded-lg bg-n-alpha-1"
            >
              <div class="flex items-center gap-2">
                <div class="w-40 shrink-0">
                  <ComboBox
                    v-model="button.type"
                    :options="buttonTypeOptions"
                  />
                </div>
                <Input
                  v-if="button.type !== 'COPY_CODE'"
                  v-model="button.text"
                  :placeholder="t('WASAPI_TEMPLATES.DIALOG.BUTTON_TEXT')"
                  class="flex-1"
                />
                <Button
                  variant="ghost"
                  color="ruby"
                  size="sm"
                  icon="i-lucide-trash-2"
                  @click="removeButton(index)"
                />
              </div>
              <Input
                v-if="button.type === 'URL'"
                v-model="button.url"
                :placeholder="t('WASAPI_TEMPLATES.DIALOG.BUTTON_URL')"
              />
              <Input
                v-if="button.type === 'PHONE_NUMBER'"
                v-model="button.phoneNumber"
                :placeholder="t('WASAPI_TEMPLATES.DIALOG.BUTTON_PHONE')"
              />
              <Input
                v-if="button.type === 'COPY_CODE'"
                v-model="button.example"
                :placeholder="t('WASAPI_TEMPLATES.DIALOG.BUTTON_CODE')"
              />
            </div>
          </div>
        </div>

        <div class="col-span-2">
          <TemplatePreview :form="form" />
        </div>
      </div>

      <div
        v-if="apiError"
        class="flex items-start gap-2 p-2.5 text-xs rounded-lg bg-n-ruby-3/50 text-n-ruby-11"
      >
        <Icon icon="i-lucide-triangle-alert" class="size-3.5 shrink-0 mt-0.5" />
        <span>{{ apiError }}</span>
      </div>
      <ul
        v-if="errorMessages.length && step === 2"
        class="flex flex-col gap-1 p-2.5 text-xs rounded-lg text-n-ruby-11 bg-n-ruby-3/40"
      >
        <li v-for="message in errorMessages" :key="message">{{ message }}</li>
      </ul>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          v-if="step === 2"
          variant="faded"
          color="slate"
          :label="t('WASAPI_COMMON.BACK')"
          type="button"
          @click="step = 1"
        />
        <div class="flex justify-end flex-1 gap-3">
          <Button
            variant="faded"
            color="slate"
            :label="t('WASAPI_COMMON.CANCEL')"
            type="button"
            @click="close"
          />
          <Button
            v-if="step === 1"
            color="blue"
            :label="t('WASAPI_COMMON.NEXT')"
            type="button"
            @click="step = 2"
          />
          <Button
            v-else
            color="blue"
            :label="
              isEditing
                ? t('WASAPI_TEMPLATES.DIALOG.SAVE')
                : t('WASAPI_TEMPLATES.DIALOG.SUBMIT')
            "
            :is-loading="isSaving"
            :disabled="!canSubmit || isSaving"
            type="button"
            @click="submit"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
